/* Cortazu quick add v3 (tab 3 b). Cortazu file, not part of Hyper.
   - Desktop: size chips on the product card (snippets/cz-quick-sizes.liquid, mode "card"). A chip is a submit
     button of a Hyper product-form: the click sets the variant id, ProductForm adds it and opens the cart drawer.
   - Mobile: bottom sheet inside the quick view (mode "sheet"): colour siblings, size with stock note, add.
   - Desktop quick view: a colour tile (cz-swatches, 'pdp') loads the sibling into the same modal (20 Oct 2026).
   Loaded once per page by sections/quick-view.liquid.
   28 Oct 2026 (q177): a sold-out size (.is-sold-out, card and sheet) opens the compact inline Notify me
   (assets/cz-notify-inline.js, loaded on first use only; markup + config: snippets/cz-quick-sizes.liquid).
   28 Oct 2026 (owner): the same Notify me in the desktop quick view (Hyper variant picker; snippets/cz-qv-notify.liquid).
   29 Oct 2026 (audit B5): desktop quick view starts without a size, button "Choose your size" (like PDP and sheet). */
(() => {
  if (window.czQuickAdd) return;
  window.czQuickAdd = true;

  const LOADING_TIMEOUT = 10000;

  /* ---------- Notify me on a sold-out size (28 Oct, q177) ---------- */

  // Lazy loader, shared with assets/cz-set.js (whichever loads first defines it). src from [data-cz-notify-src].
  window.czNotifyLoad =
    window.czNotifyLoad ||
    ((el) => {
      if (window.czNotifyInline) return Promise.resolve(window.czNotifyInline);
      if (!window.czNotifyLoading) {
        const src = el?.closest?.('[data-cz-notify-src]')?.getAttribute('data-cz-notify-src');
        if (!src) return Promise.reject(new Error('cz-notify-inline: no src'));
        window.czNotifyLoading = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.onload = () => resolve(window.czNotifyInline);
          script.onerror = (error) => {
            window.czNotifyLoading = null;
            reject(error);
          };
          document.head.appendChild(script);
        });
      }
      return window.czNotifyLoading;
    });

  const openNotify = (chip, options) => {
    window
      .czNotifyLoad(chip)
      .then((notify) => {
        if (notify && chip.isConnected) notify.open(chip, options);
      })
      .catch(() => {});
  };
  const closeNotify = (scope) => window.czNotifyInline?.close(scope);

  // Start loading as the press on a sold-out size begins.
  document.addEventListener('pointerdown', (event) => {
    const chip = event.target.closest?.('.cz-qa__size.is-sold-out, .cz-qs__size.is-sold-out');
    if (chip) window.czNotifyLoad(chip).catch(() => {});
  });

  /* ---------- Desktop: card chips ---------- */

  const clearLoading = () => {
    document.querySelectorAll('.cz-qa.is-loading').forEach((form) => {
      form.classList.remove('is-loading');
      form.querySelectorAll('.cz-qa__size.is-selected').forEach((chip) => chip.classList.remove('is-selected'));
    });
  };

  const setNote = (sizes, chip) => {
    const note = sizes.closest('.cz-qa')?.querySelector('[data-cz-qa-note]');
    if (!note) return;
    const text = (chip && chip.dataset.lowNote) || note.dataset.defaultNote || '';
    if (note.textContent !== text) note.textContent = text;
  };

  document.addEventListener('click', (event) => {
    const chip = event.target.closest('.cz-qa__size');
    if (!chip) return;
    if (chip.classList.contains('is-sold-out')) {
      event.preventDefault();
      openNotify(chip, { focus: true });
      return;
    }
    const form = chip.closest('form.cz-qa');
    if (!form || chip.disabled || form.classList.contains('is-loading')) {
      event.preventDefault();
      return;
    }
    const input = form.querySelector('input[name="id"]');
    if (input) input.value = chip.dataset.variantId;
    form.querySelectorAll('.cz-qa__size.is-selected').forEach((el) => el.classList.remove('is-selected'));
    chip.classList.add('is-selected');
    form.classList.add('is-loading');
    clearTimeout(form.czTimer);
    form.czTimer = setTimeout(clearLoading, LOADING_TIMEOUT);
    // ProductForm handles the submit that follows this click (AJAX add, cart update, drawer).
  });

  document.addEventListener('product-ajax:added', clearLoading);
  document.addEventListener('product-ajax:error', clearLoading);

  // Low-stock note follows the chip under the pointer or focus.
  document.addEventListener('pointerover', (event) => {
    const chip = event.target.closest?.('.cz-qa__size');
    if (chip) setNote(chip.parentElement, chip);
  });
  document.addEventListener('focusin', (event) => {
    const chip = event.target.closest?.('.cz-qa__size');
    if (chip) setNote(chip.parentElement, chip);
  });
  document.addEventListener('pointerout', (event) => {
    const sizes = event.target.closest?.('[data-cz-qa-sizes]');
    if (sizes && !sizes.contains(event.relatedTarget)) setNote(sizes, null);
  });

  // One tab stop per card; arrow keys move between the available sizes (roving tabindex).
  document.addEventListener('keydown', (event) => {
    const chip = event.target.closest?.('.cz-qa__size');
    if (!chip) return;
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const chips = Array.from(chip.parentElement.querySelectorAll('.cz-qa__size:not([disabled])'));
    if (!chips.length) return;
    let index = chips.indexOf(chip);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = chips.length - 1;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') index = (index + 1) % chips.length;
    else index = (index - 1 + chips.length) % chips.length;
    event.preventDefault();
    chips.forEach((el) => el.setAttribute('tabindex', '-1'));
    chips[index].setAttribute('tabindex', '0');
    chips[index].focus();
  });

  /* ---------- Mobile: bottom sheet in the quick view ---------- */

  const selectSize = (button, viaKey) => {
    const sheet = button.closest('[data-cz-qs]');
    if (!sheet || button.disabled) return;
    sheet.querySelectorAll('.cz-qs__size[aria-checked="true"]').forEach((el) => el.setAttribute('aria-checked', 'false'));
    button.setAttribute('aria-checked', 'true');
    // 28 Oct (q177): a sold-out size -> no variant to add, the inline Notify me under the sizes instead.
    if (button.classList.contains('is-sold-out')) {
      const soldInput = sheet.querySelector('[data-cz-qs-id]');
      if (soldInput) soldInput.value = '';
      const soldCta = sheet.querySelector('[data-cz-qs-cta]');
      if (soldCta) {
        soldCta.disabled = true;
        const soldLabel = soldCta.querySelector('.btn__text') || soldCta;
        if (sheet.dataset.labelChoose) soldLabel.textContent = sheet.dataset.labelChoose;
      }
      const soldNote = sheet.querySelector('[data-cz-qs-note]');
      if (soldNote) soldNote.textContent = '';
      sheet.querySelector('.product-form__error-message')?.setAttribute('hidden', '');
      openNotify(button, { focus: !viaKey, toggle: false });
      return;
    }
    closeNotify(sheet);
    const input = sheet.querySelector('[data-cz-qs-id]');
    if (input) input.value = button.dataset.variantId;
    const price = button.dataset.price || '';
    const priceEl = sheet.querySelector('[data-cz-qs-price]');
    if (priceEl && price) priceEl.textContent = price;
    const cta = sheet.querySelector('[data-cz-qs-cta]');
    if (cta) {
      cta.disabled = false;
      cta.removeAttribute('aria-disabled');
      const label = cta.querySelector('.btn__text') || cta;
      label.textContent = (sheet.dataset.labelAdd || '%p').replace('%p', price);
    }
    sheet.querySelector('.product-form__error-message')?.setAttribute('hidden', '');
    // 22 Oct: "Only n left" of the picked size under the sizes (the size itself shows the low-stock dot)
    const note = sheet.querySelector('[data-cz-qs-note]');
    if (note) note.textContent = button.dataset.lowNote || '';
  };

  document.addEventListener('click', (event) => {
    const size = event.target.closest('.cz-qs__size');
    if (size) {
      event.preventDefault();
      selectSize(size);
    }
  });

  // Radio group keyboard support for the sheet sizes.
  document.addEventListener('keydown', (event) => {
    const size = event.target.closest?.('.cz-qs__size');
    if (!size || !['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const sizes = Array.from(size.parentElement.querySelectorAll('.cz-qs__size:not([disabled])'));
    if (!sizes.length) return;
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = sizes[(sizes.indexOf(size) + step + sizes.length) % sizes.length];
    event.preventDefault();
    next.focus();
    selectSize(next, true);
  });

  const quickViewSectionId = () => {
    if (window.FoxTheme && FoxTheme.QuickViewSectionId) return FoxTheme.QuickViewSectionId;
    const section = document.querySelector('.section-group-overlay-quick-view');
    if (!section) return null;
    return window.FoxTheme?.utils?.getSectionId
      ? FoxTheme.utils.getSectionId(section)
      : section.id.replace('shopify-section-', '');
  };

  const siblingCache = new Map();

  // Colour dots: load the sibling's sheet into the open quick view instead of leaving the page.
  document.addEventListener('click', async (event) => {
    const dot = event.target.closest('[data-cz-qs-sibling]');
    if (!dot) return;
    const sheet = dot.closest('[data-cz-qs]');
    const sectionId = quickViewSectionId();
    if (!sheet || !sectionId || dot.getAttribute('aria-current') === 'true') {
      if (dot.getAttribute('aria-current') === 'true') event.preventDefault();
      return;
    }
    event.preventDefault();
    const url = `${dot.getAttribute('href').split('?')[0]}?section_id=${sectionId}`;
    sheet.setAttribute('aria-busy', 'true');
    try {
      if (!siblingCache.has(url)) {
        siblingCache.set(
          url,
          fetch(url).then((response) => {
            if (!response.ok) throw new Error(`Quick add colour request failed: ${response.status}`);
            return response.text();
          })
        );
      }
      const html = await siblingCache.get(url);
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cz-qs]');
      if (!next) throw new Error('Quick add sheet not found');
      sheet.dataset.productUrl = next.dataset.productUrl;
      sheet.dataset.labelAdd = next.dataset.labelAdd;
      sheet.dataset.labelChoose = next.dataset.labelChoose;
      sheet.innerHTML = next.innerHTML;
      const modal = sheet.closest('quick-view-modal');
      if (modal && next.dataset.productUrl) modal.dataset.productUrl = next.dataset.productUrl;
      sheet.querySelector('[data-cz-qs-sibling][aria-current="true"]')?.focus();
    } catch (error) {
      siblingCache.delete(url);
      console.error(error);
      window.location.href = dot.href;
    } finally {
      sheet.removeAttribute('aria-busy');
    }
  });

  /* ---------- Desktop: colour tiles inside a quick view (snippets/cz-swatches.liquid, context 'pdp') ----------
     20 Oct 2026: a colour tile in a quick view (product card or cart upsell) used to be a plain link to the
     product page, which closed the quick view and left the cart. Now the sibling product loads into the same
     modal (QuickViewModal.loadContent/renderContent, assets/quick-view.js); the link stays the fallback. */
  document.addEventListener('click', async (event) => {
    const tile = event.target.closest('quick-view-modal a.cz-sw__dot');
    if (!tile || tile.closest('[data-cz-qs]')) return;
    const modal = tile.closest('quick-view-modal');
    if (!modal || typeof modal.loadContent !== 'function' || typeof modal.renderContent !== 'function') return;
    event.preventDefault();
    if (tile.getAttribute('aria-current') || tile.classList.contains('is-current')) return;
    const href = tile.getAttribute('href').split('?')[0];
    const list = tile.closest('.cz-sw');
    if (list && list.getAttribute('aria-busy') === 'true') return;
    if (list) list.setAttribute('aria-busy', 'true');
    const previousUrl = modal.dataset.productUrl;
    modal.dataset.productUrl = href;
    try {
      const content = await modal.loadContent();
      // Superseded by another tile or closed meanwhile: don't render stale content.
      if (modal.dataset.productUrl !== href || !modal.open) return;
      modal.renderContent(content);
      modal.querySelector('.cz-sw--pdp a.cz-sw__dot[aria-current]')?.focus({ preventScroll: true });
    } catch (error) {
      console.error(error);
      modal.dataset.productUrl = previousUrl;
      window.location.href = href;
    } finally {
      if (list && list.isConnected) list.removeAttribute('aria-busy');
    }
  });

  /* ---------- Desktop quick view: Notify me on a sold-out size (28 Oct 2026, owner) ----------
     The desktop quick view keeps Hyper's variant picker, where a sold-out size can be picked (Hyper then shows a
     disabled "Sold out"). Now the same inline Notify me as the mobile sheet and the card panel opens below the sizes
     (slot in snippets/cz-qv-notify.liquid, box from assets/cz-notify-inline.js) and the button reads "Choose your
     size" like the sheet. Hyper morphs the picker in place, so the size radio stays the chip; the buy_buttons block
     is not re-rendered, so the box survives the section render. Runs on FoxTheme's variantChange, which Hyper
     publishes on the optimistic update and again after the render. */
  let qvPointer = false;
  document.addEventListener(
    'pointerdown',
    (event) => {
      const label = event.target.closest?.('quick-view-modal variant-selects label');
      qvPointer = !!label;
      if (!label) return;
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
      const holder = label.closest('product-info')?.querySelector('[data-cz-qv-notify]');
      // Start loading the script as the press on a sold-out size begins.
      if (holder && input && input.classList.contains('disabled')) window.czNotifyLoad(holder).catch(() => {});
    },
    true
  );
  document.addEventListener(
    'keydown',
    () => {
      qvPointer = false;
    },
    true
  );

  const qvHolder = (sectionId) =>
    Array.from(document.querySelectorAll('quick-view-modal [data-cz-qv-notify]')).find(
      (el) => el.dataset.sectionId === sectionId && el.offsetParent !== null
    ) || null;

  const qvSizeChip = (holder) => {
    const radios = holder.closest('product-info')?.querySelectorAll('variant-selects input[type="radio"]:checked') || [];
    return Array.from(radios).find((input) => input.dataset.optionName === holder.dataset.sizeName) || null;
  };

  const qvCloseNotify = (holder) => {
    delete holder.dataset.openVariant;
    closeNotify(holder);
  };

  const onQuickViewVariant = ({ data } = {}) => {
    if (!data || data.sectionId == null) return;
    const holder = qvHolder(String(data.sectionId));
    if (!holder) return;
    const variant = data.variant;
    if (!variant || variant.available) {
      qvCloseNotify(holder);
      return;
    }
    const chip = qvSizeChip(holder);
    if (!chip) {
      qvCloseNotify(holder);
      return;
    }
    const variantId = String(variant.id);
    if (holder.dataset.openVariant && holder.dataset.openVariant !== variantId) qvCloseNotify(holder);
    holder.dataset.openVariant = variantId;
    const size = Array.isArray(variant.options) ? variant.options[Number(holder.dataset.sizeIndex)] : '';
    chip.dataset.czNotifyVariant = variantId;
    chip.dataset.czNotifySize = size || chip.value || '';

    // Same button text as the sheet while other sizes are in stock (Hyper has just set "Sold out").
    if (holder.dataset.productAvailable === 'true') {
      const modal = holder.closest('quick-view-modal');
      const choose = modal?.querySelector('[data-cz-qs]')?.dataset.labelChoose;
      const label = modal?.querySelector(`#ProductSubmitButton-${holder.dataset.sectionId} .btn__text, #product-form-${holder.dataset.sectionId} [name="add"] > span`);
      if (choose && label) label.textContent = choose;
    }

    const focus = qvPointer;
    window
      .czNotifyLoad(holder)
      .then((notify) => {
        if (!notify || !holder.isConnected || !chip.isConnected || holder.dataset.openVariant !== variantId) return;
        notify.open(chip, { host: holder, focus: false, toggle: false });
        // After Hyper's own focus on the picked size (it follows the render): focus the field, like the sheet.
        if (focus) setTimeout(() => holder.querySelector('.cz-ni__input')?.focus({ preventScroll: true }), 0);
      })
      .catch(() => {});
  };

  const subscribeQuickView = () => {
    if (!window.FoxTheme?.pubsub) return false;
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, onQuickViewVariant);
    return true;
  };
  if (!subscribeQuickView()) document.addEventListener('DOMContentLoaded', subscribeQuickView, { once: true });

  /* ---------- Desktop quick view: no size preselected (29 Oct 2026, audit B5) ----------
     Hyper checks the first available size in the quick view, so ADD TO CART was live for a size nobody chose.
     Same rule as the PDP (assets/cz-pdp-nosize.js, q123) and the mobile sheet: on every render of a quick view
     (also after a colour tile) the size radios are unchecked, the button reads "Choose your size" and a click
     without a size shows a hint instead of adding. Picking a size hands control back to Hyper. A URL with
     ?variant= keeps its size. Styles: assets/cz-quick-add.css (.cz-need-size inside quick-view-modal). */
  const QV_SIZE_NAMES = ['size', 'maat', 'größe', 'grösse', 'taille'];
  const qvNoSize = (modal) => {
    if (!modal || /[?&]variant=/.test(modal.dataset.productUrl || '')) return;
    const selects = modal.querySelector('variant-selects');
    const form = selects && modal.querySelector(`#product-form-${CSS.escape(selects.dataset.section || '')}`);
    if (!form || form.dataset.czNoSize === 'done') return;
    const fieldset = Array.from(selects.querySelectorAll('fieldset')).find((fs) => {
      const input = fs.querySelector('input[type="radio"]');
      return input && QV_SIZE_NAMES.includes((input.dataset.optionName || '').toLowerCase());
    });
    if (!fieldset) return;
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    if (radios.length < 2) return;
    form.dataset.czNoSize = 'done';
    const lang = (document.documentElement.lang || 'en').slice(0, 2);
    const choose =
      modal.querySelector('[data-cz-qs]')?.dataset.labelChoose ||
      { nl: 'Kies je maat', de: 'Größe wählen' }[lang] ||
      'Choose your size';
    const hintText = { nl: 'Kies eerst je maat', de: 'Bitte zuerst eine Größe wählen' }[lang] || 'Please choose your size first';

    radios.forEach((radio) => (radio.checked = false));
    const shown = fieldset.querySelector('[data-selected-swatch-value]');
    if (shown) shown.textContent = '';
    form.classList.add('cz-need-size');
    const btn = form.querySelector('[name="add"]');
    if (btn && !btn.querySelector('.cz-need-size__label')) {
      const label = document.createElement('span');
      label.className = 'cz-need-size__label';
      label.textContent = choose;
      btn.appendChild(label);
    }
    const hint = document.createElement('p');
    hint.className = 'cz-need-size__hint';
    hint.setAttribute('role', 'alert');
    hint.hidden = true;
    hint.textContent = hintText;
    fieldset.appendChild(hint);

    // Capture on the form: runs before Hyper's own change handler publishes the variant (see audit B6).
    const scope = form.closest('product-info, .product__info-container') || modal;
    const chosen = (event) => {
      if (!radios.includes(event.target)) return;
      scope.removeEventListener('change', chosen, true);
      form.classList.remove('cz-need-size');
      hint.hidden = true;
    };
    scope.addEventListener('change', chosen, true);
    const block = (event) => {
      if (!form.classList.contains('cz-need-size')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      hint.hidden = false;
      (fieldset.querySelector('input[type="radio"]:not(.disabled)') || radios[0])?.focus({ preventScroll: true });
    };
    form.addEventListener('submit', block, true);
    btn?.addEventListener('click', block, true);
  };

  customElements.whenDefined('quick-view-modal').then(() => {
    const QuickView = customElements.get('quick-view-modal');
    const render = QuickView?.prototype?.renderContent;
    if (typeof render !== 'function' || render.czNoSize) return;
    const wrapped = function (...args) {
      const result = render.apply(this, args);
      const run = () => qvNoSize(this);
      if (result && typeof result.then === 'function') result.then(run, () => {});
      else run();
      return result;
    };
    wrapped.czNoSize = true;
    QuickView.prototype.renderContent = wrapped;
  });

  // Cart changes make cached sibling stock stale.
  if (window.FoxTheme?.pubsub) {
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => siblingCache.clear());
  }

  // Swipe the sheet down by its grab handle or header to close it.
  let drag = null;
  document.addEventListener(
    'touchstart',
    (event) => {
      const grip = event.target.closest?.('.cz-qs__handle, .cz-qs__head');
      if (!grip) return;
      const inner = grip.closest('.drawer__inner');
      if (!inner) return;
      drag = { inner, startY: event.touches[0].clientY, dy: 0 };
    },
    { passive: true }
  );
  document.addEventListener(
    'touchmove',
    (event) => {
      if (!drag) return;
      drag.dy = Math.max(0, event.touches[0].clientY - drag.startY);
      drag.inner.style.transition = 'none';
      drag.inner.style.transform = `translateY(${drag.dy}px)`;
    },
    { passive: true }
  );
  document.addEventListener('touchend', () => {
    if (!drag) return;
    const { inner, dy } = drag;
    drag = null;
    inner.style.transition = '';
    inner.style.transform = '';
    if (dy > 90) inner.closest('quick-view-modal')?.hide();
  });
})();
